import { Search } from "lucide-react";

import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";
import type {
  DirectionFilter,
  StatusFilter,
  SMSStatusFilter,
} from "@/app/_module/types/communication";
import type { EmailStatus } from "../types/email-history.types";
import type { DurationFilter } from "../types/communication-history.types";

export type EmailStatusFilter = EmailStatus | "all";

interface CommunicationFiltersProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  directionFilter: DirectionFilter;
  onDirectionChange: (direction: DirectionFilter) => void;
  statusFilter?: StatusFilter | SMSStatusFilter | EmailStatusFilter;
  onStatusChange?: (status: StatusFilter | SMSStatusFilter | EmailStatusFilter) => void;
  type: "calls" | "sms" | "emails";
  callDurationFilter?: DurationFilter;
  onDurationChange?: (duration: DurationFilter) => void;
}

export const CommunicationFilters = function({
  searchQuery,
  onSearchChange,
  directionFilter,
  onDirectionChange,
  statusFilter,
  onStatusChange,
  type,
  callDurationFilter,
  onDurationChange,
}: CommunicationFiltersProps) {
  const durationPresets: { value: DurationFilter; label: string }[] = [
    { value: "all", label: "Any Duration" },
    { value: "30s", label: "> 30s" },
    { value: "60s", label: "> 1 min" },
    { value: "2min", label: "> 2 min" },
    { value: "5min", label: "> 5 min" },
  ];
  const callStatuses: StatusFilter[] = [
    "all",
    "answered",
    "no-answer",
    "missed",
    "declined",
    "failed",
    "busy",
  ];

  const statusLabelOverrides: Record<string, string> = {
    "no-answer": "No Answer",
  };
  const smsStatuses: SMSStatusFilter[] = [
    "all",
    "sent",
    "delivered",
    "failed",
    "received",
  ];
  const emailStatuses: EmailStatusFilter[] = [
    "all",
    "sent",
    "delivered",
    "received",
    "opened",
    "failed",
  ];

  const statuses =
    type === "calls"
      ? callStatuses
      : type === "sms"
        ? smsStatuses
        : emailStatuses;

  const searchPlaceholder =
    type === "calls"
      ? "Find a phone number or lead name..."
      : type === "sms"
        ? "Find a phone number, message text or lead name..."
        : "Find an email address, subject or lead name...";

  return (
    <div className="space-y-4">
      {/* Search bar + duration dropdown */}
      <div className="flex items-center gap-2">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            className="pl-10 bg-muted/50"
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            value={searchQuery}
          />
        </div>
        {type === "calls" && onDurationChange && (
          <Select
            value={callDurationFilter || "all"}
            onValueChange={(value) => onDurationChange(value as DurationFilter)}
          >
            <SelectTrigger className="w-[140px] h-9 bg-muted/50">
              <SelectValue placeholder="Duration" />
            </SelectTrigger>
            <SelectContent>
              {durationPresets.map((preset) => (
                <SelectItem key={preset.value} value={preset.value}>
                  {preset.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Filter buttons - combined into single row */}
      <div className="flex flex-wrap gap-1">
        {/* All filter - resets direction, status, and duration */}
        <Button
          onClick={() => {
            onDirectionChange("all");
            if (onStatusChange) onStatusChange("all");
            if (onDurationChange) onDurationChange("all");
          }}
          size="sm"
          variant={directionFilter === "all" && statusFilter === "all" && (!callDurationFilter || callDurationFilter === "all") ? "default" : "outline"}
          className={
            directionFilter === "all" && statusFilter === "all" && (!callDurationFilter || callDurationFilter === "all")
              ? "bg-primary text-primary-foreground"
              : ""
          }
        >
          All
        </Button>

        {/* Direction filters */}
        <Button
          onClick={() => onDirectionChange("inbound")}
          size="sm"
          variant={directionFilter === "inbound" ? "default" : "outline"}
          className={
            directionFilter === "inbound"
              ? "bg-blue-600 hover:bg-blue-700 text-white"
              : ""
          }
        >
          Inbound
        </Button>
        <Button
          onClick={() => onDirectionChange("outbound")}
          size="sm"
          variant={directionFilter === "outbound" ? "default" : "outline"}
          className={
            directionFilter === "outbound"
              ? "bg-green-600 hover:bg-green-700 text-white"
              : ""
          }
        >
          Outbound
        </Button>

        {/* Separator */}
        <div className="w-px bg-border mx-1" />

        {/* Status filters (excluding "all" since we have a combined All button) */}
        {!!statusFilter && !!onStatusChange && (
          <>
            {statuses.filter(s => s !== "all").map((status: typeof statusFilter) => (
              <Button
                key={status}
                onClick={() => onStatusChange(status)}
                size="sm"
                variant={statusFilter === status ? "default" : "outline"}
                className={
                  statusFilter === status
                    ? "bg-primary text-primary-foreground"
                    : ""
                }
              >
                {statusLabelOverrides[status as string] || (status.charAt(0).toUpperCase() + status.slice(1))}
              </Button>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
